/*  Colega Link · Admin · Consola Comercial (Mando Total)
    Ruta: /app/admin/configuracion
    Edita en caliente TODOS los parámetros de cobro de la plataforma:
      • Mediaciones familiares (cargo fijo, %, topes, UF, retraso Free)
      • Membresías de abogados (Pionero mensual/anual · Premium mensual/anual)
      • Comisiones por delegación (Free / Pionero activo / Pionero post / Premium / Fundador)
      • Colega IA (consultas incluidas · pack extra · precio UF/CLP override)
    Al guardar, el backend rebindea las constantes en memoria y toda la
    plataforma responde con los nuevos valores en tiempo real. El endpoint
    `/api/config/public` expone los mismos valores al frontend público
    (Pricing, MembershipReserve, formularios, calculadoras). */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import { useConfig } from "@/contexts/ConfigContext";
import {
  Save, RefreshCw, Loader2, ShieldAlert, Check, ChevronDown, ChevronRight,
  Scale, Users, Sparkles, DollarSign,
} from "lucide-react";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);
const PCT = (dec) => `${Math.round((Number(dec) || 0) * 10000) / 100}%`;
const UF_FMT = (n) =>
  new Intl.NumberFormat("es-CL", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(Number(n) || 0);

// Formato tolerante para inputs numéricos
const toInt = (v) => {
  const n = parseInt(String(v).replace(/[^\d-]/g, "") || "0", 10);
  return Number.isFinite(n) ? n : 0;
};
const toFloat = (v) => {
  const n = parseFloat(String(v).replace(/[^\d.-]/g, "") || "0");
  return Number.isFinite(n) ? n : 0;
};

// Validación previa a envío
function validate(cfg) {
  const errs = [];
  const zeroToOne = (k, label) => {
    const v = Number(cfg[k]);
    if (Number.isNaN(v) || v < 0 || v > 1) errs.push(`${label}: debe ser un decimal entre 0 y 1 (ej: 0.08 = 8%).`);
  };
  const posInt = (k, label, max = 10_000_000) => {
    const v = Number(cfg[k]);
    if (!Number.isFinite(v) || v < 0 || v > max) errs.push(`${label}: debe ser un entero entre 0 y ${max.toLocaleString("es-CL")}.`);
  };
  posInt("cargo_fijo_mediacion", "Cargo fijo mediación", 1_000_000);
  zeroToOne("porcentaje_comision_acuerdo", "% Comisión por acuerdo");
  posInt("tope_total_solicitante_clp", "Tope total al solicitante");
  posInt("max_mediator_session_fee_clp", "Tope por sesión mediador");
  posInt("plan_pionero_monthly_clp", "Pionero · mensual");
  posInt("plan_pionero_annual_clp", "Pionero · anual");
  posInt("plan_premium_monthly_clp", "Premium · mensual");
  posInt("plan_premium_annual_clp", "Premium · anual");
  zeroToOne("comision_delegacion_free", "Comisión delegación · Free");
  zeroToOne("comision_delegacion_pionero_activo", "Comisión delegación · Pionero activo");
  zeroToOne("comision_delegacion_pionero_post", "Comisión delegación · Pionero post");
  zeroToOne("comision_delegacion_premium", "Comisión delegación · Premium");
  zeroToOne("comision_delegacion_founder", "Comisión delegación · Fundador");
  const aiInc = Number(cfg.ai_included_queries);
  if (!Number.isFinite(aiInc) || aiInc < 0 || aiInc > 100_000) errs.push("IA · Consultas incluidas: entero entre 0 y 100.000.");
  const aiSize = Number(cfg.ai_extra_pack_size);
  if (!Number.isFinite(aiSize) || aiSize < 1 || aiSize > 10_000) errs.push("IA · Tamaño Pack Extra: entero entre 1 y 10.000.");
  const aiUf = Number(cfg.ai_extra_pack_uf);
  if (!Number.isFinite(aiUf) || aiUf < 0 || aiUf > 100) errs.push("IA · Precio Pack Extra (UF): decimal entre 0 y 100.");
  posInt("ai_extra_pack_clp_override", "IA · Precio Pack Extra CLP override");
  return errs;
}

export default function AdminBusinessConfig() {
  const { config: publicConfig, refresh: refreshConfig } = useConfig();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [flash, setFlash] = useState({ type: "", text: "" });
  const [open, setOpen] = useState({ mediaciones: true, membresias: true, ia: true });
  // UF del día — se refresca cuando el ConfigContext se actualiza.
  const ufValue = Number(publicConfig?.uf_value_clp) || 39174;
  const ufUpdatedAt = publicConfig?.uf_updated_at;

  const load = async () => {
    setLoading(true); setFlash({ type: "", text: "" });
    try {
      const { data } = await api.get("/admin/business-config");
      setConfig(data.config);
      setDefaults(data.defaults);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    } catch (err) {
      setFlash({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const save = async () => {
    const errs = validate(config);
    if (errs.length > 0) {
      setFlash({ type: "error", text: `Corrige antes de guardar: ${errs[0]}` });
      return;
    }
    setSaving(true); setFlash({ type: "", text: "" });
    try {
      const payload = {
        // Mediaciones
        cargo_fijo_mediacion: toInt(config.cargo_fijo_mediacion),
        porcentaje_comision_acuerdo: toFloat(config.porcentaje_comision_acuerdo),
        usar_uf: !!config.usar_uf,
        tope_total_solicitante_clp: toInt(config.tope_total_solicitante_clp),
        max_mediator_session_fee_clp: toInt(config.max_mediator_session_fee_clp),
        minutos_retraso_free_abogados: toInt(config.minutos_retraso_free_abogados),
        // Membresías
        plan_pionero_monthly_clp: toInt(config.plan_pionero_monthly_clp),
        plan_pionero_annual_clp: toInt(config.plan_pionero_annual_clp),
        plan_premium_monthly_clp: toInt(config.plan_premium_monthly_clp),
        plan_premium_annual_clp: toInt(config.plan_premium_annual_clp),
        // Comisiones
        comision_delegacion_free: toFloat(config.comision_delegacion_free),
        comision_delegacion_pionero_activo: toFloat(config.comision_delegacion_pionero_activo),
        comision_delegacion_pionero_post: toFloat(config.comision_delegacion_pionero_post),
        comision_delegacion_premium: toFloat(config.comision_delegacion_premium),
        comision_delegacion_founder: toFloat(config.comision_delegacion_founder),
        // Colega IA
        ai_included_queries: toInt(config.ai_included_queries),
        ai_extra_pack_size: toInt(config.ai_extra_pack_size),
        ai_extra_pack_uf: toFloat(config.ai_extra_pack_uf),
        ai_extra_pack_clp_override: toInt(config.ai_extra_pack_clp_override),
      };
      const { data } = await api.put("/admin/business-config", payload);
      setConfig(data.config);
      setMeta({ updated_at: data.applied_at, updated_by: "you" });
      setFlash({ type: "success", text: "✓ Consola Comercial aplicada. Los cambios ya rigen en toda la plataforma." });
      if (typeof refreshConfig === "function") { try { await refreshConfig(); } catch { /* noop */ } }
      setTimeout(() => setFlash({ type: "", text: "" }), 6000);
    } catch (err) {
      setFlash({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally { setSaving(false); }
  };

  const resetToDefaults = () => {
    if (!defaults) return;
    if (!window.confirm("¿Reponer los valores por defecto en el formulario? (Aún debes presionar Guardar para aplicarlos).")) return;
    setConfig({ ...defaults });
  };

  return (
    <div className="container-app py-10 max-w-4xl" data-testid="admin-business-config">
      <AdminNav/>
      <header className="flex items-center gap-3 mb-6">
        <DollarSign size={26} className="text-cyan-300"/>
        <div className="flex-1">
          <h1 className="serif text-3xl leading-tight">Consola Comercial</h1>
          <p className="text-sm text-muted-foreground">
            Mando total sobre TODOS los parámetros de cobro: mediaciones, membresías, comisiones y Colega IA. Los cambios se aplican en tiempo real a formularios, landing de planes, calculadoras y procesos de pago.
          </p>
        </div>
      </header>

      {/* Banner UF del día · fuente única de conversión CLP↔UF */}
      <div className="mb-5 p-3 rounded-md border border-cyan-400/25 bg-cyan-400/[0.05] text-xs text-cyan-100 flex items-center justify-between flex-wrap gap-2" data-testid="cfg-uf-banner">
        <div>
          <strong>Valor UF del día:</strong> {CLP(ufValue)} · fuente <a href="https://mindicador.cl/api/uf" target="_blank" rel="noreferrer" className="underline">mindicador.cl</a>
          {ufUpdatedAt && <span className="text-cyan-200/70"> · actualizado {new Date(ufUpdatedAt).toLocaleDateString("es-CL")}</span>}
        </div>
        <div className="text-cyan-200/70">
          Toda casilla monetaria acepta <strong>CLP</strong> o <strong>UF</strong>.
        </div>
      </div>

      {loading || !config ? (
        <div className="py-16 text-center text-muted-foreground">Cargando…</div>
      ) : (
        <div className="space-y-5">
          {flash.text && (
            <div className={`p-3 rounded-md text-sm ${flash.type === "success" ? "bg-emerald-400/10 border border-emerald-400/30 text-emerald-200" : "bg-red-400/10 border border-red-400/30 text-red-200"}`} data-testid="cfg-flash">
              {flash.text}
            </div>
          )}

          {/* ═══════════════ Mediaciones Familiares ═══════════════ */}
          <Section
            id="mediaciones"
            open={open.mediaciones}
            onToggle={() => toggle("mediaciones")}
            icon={<Scale size={18} className="text-cyan-300"/>}
            title="Mediaciones Familiares"
            testid="cfg-section-mediaciones"
          >
            <Field
              label="Cargo Fijo ColegaLink (mediación inicial)"
              hint="Se cobra al solicitante en el Pago Protegido inicial de cada mediación familiar. Ingresá el valor en CLP o UF; ambos campos se sincronizan."
            >
              <MoneyInput value={config.cargo_fijo_mediacion} onChange={(v) => set("cargo_fijo_mediacion", v)}
                uf={ufValue} testid="cfg-cargo-fijo"/>
            </Field>
            <Field
              label="% Comisión por Acuerdo (decimal)"
              hint="Ingresá 0.08 para 8%. Se aplica ÚNICAMENTE al saldo pendiente entre honorario base y honorario total con acuerdo."
            >
              <NumericInput value={config.porcentaje_comision_acuerdo} onChange={(v) => set("porcentaje_comision_acuerdo", v)}
                step={0.001} min={0} max={1} decimal
                suffix={<span className="text-xs text-muted-foreground">= {PCT(config.porcentaje_comision_acuerdo)}</span>}
                testid="cfg-pct-comision"/>
            </Field>
            <Field
              label="Tope Legal Máximo al Solicitante"
              hint="Máximo total (pago inicial + saldo + comisión) que puede pagar la persona solicitante. Legalmente 130.602 CLP ≈ 3,33 UF."
            >
              <MoneyInput value={config.tope_total_solicitante_clp} onChange={(v) => set("tope_total_solicitante_clp", v)}
                uf={ufValue} testid="cfg-tope"/>
            </Field>
            <Field
              label="Tope por sesión del mediador"
              hint="Máximo por sesión que un mediador puede fijar en su tarifario público."
            >
              <MoneyInput value={config.max_mediator_session_fee_clp} onChange={(v) => set("max_mediator_session_fee_clp", v)}
                uf={ufValue} testid="cfg-tope-sesion"/>
            </Field>
            <Field
              label="Aviso Prioritario · retraso para abogados Free (minutos)"
              hint="Al publicar una delegación, planes de pago reciben el aviso al instante; los Free lo reciben tras estos minutos."
            >
              <NumericInput value={config.minutos_retraso_free_abogados ?? 10} onChange={(v) => set("minutos_retraso_free_abogados", v)}
                step={1} min={0} max={1440}
                suffix={<span className="text-xs text-muted-foreground">{(config.minutos_retraso_free_abogados ?? 10) === 0 ? "sin retraso" : `Free espera ${config.minutos_retraso_free_abogados ?? 10} min`}</span>}
                testid="cfg-retraso-free"/>
            </Field>
            <Field
              label="Indexación a UF"
              hint="Desactivado (feb 2026): todo en CLP. Activado: los precios muestran valor UF + CLP calculado con mindicador.cl."
            >
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!config.usar_uf} onChange={(e) => set("usar_uf", e.target.checked)}
                  className="w-4 h-4" data-testid="cfg-usar-uf"/>
                <span className={`text-sm ${config.usar_uf ? "text-emerald-300" : "text-muted-foreground"}`}>
                  {config.usar_uf ? "Activado · precios muestran UF + CLP" : "Desactivado · todo en CLP"}
                </span>
              </label>
            </Field>
          </Section>

          {/* ═══════════════ Membresías de Abogados ═══════════════ */}
          <Section
            id="membresias"
            open={open.membresias}
            onToggle={() => toggle("membresias")}
            icon={<Users size={18} className="text-cyan-300"/>}
            title="Membresías de Abogados"
            testid="cfg-section-membresias"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Plan Pionero · mensual" hint="Precio mensual del plan Pionero. Ingresá en CLP o UF.">
                <MoneyInput value={config.plan_pionero_monthly_clp} onChange={(v) => set("plan_pionero_monthly_clp", v)}
                  uf={ufValue} testid="cfg-plan-pionero-monthly"/>
              </Field>
              <Field label="Plan Pionero · anual" hint="Precio anual del plan Pionero (pago único). Ingresá en CLP o UF.">
                <MoneyInput value={config.plan_pionero_annual_clp} onChange={(v) => set("plan_pionero_annual_clp", v)}
                  uf={ufValue} testid="cfg-plan-pionero-annual"/>
              </Field>
              <Field label="Plan Premium · mensual" hint="Precio mensual del plan Premium. Ingresá en CLP o UF.">
                <MoneyInput value={config.plan_premium_monthly_clp} onChange={(v) => set("plan_premium_monthly_clp", v)}
                  uf={ufValue} testid="cfg-plan-premium-monthly"/>
              </Field>
              <Field label="Plan Premium · anual" hint="Precio anual del plan Premium (pago único). Ingresá en CLP o UF.">
                <MoneyInput value={config.plan_premium_annual_clp} onChange={(v) => set("plan_premium_annual_clp", v)}
                  uf={ufValue} testid="cfg-plan-premium-annual"/>
              </Field>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="label-eyebrow mb-1">Comisiones por Delegación (decimal 0..1)</div>
              <p className="text-[11px] text-muted-foreground mb-3">
                Porcentaje que Colega Link retiene por cada delegación exitosa, según plan del abogado delegante. Ejemplo: 0.10 = 10%.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Plan Free" hint="Comisión estándar para abogados sin membresía.">
                  <NumericInput value={config.comision_delegacion_free} onChange={(v) => set("comision_delegacion_free", v)}
                    step={0.001} min={0} max={1} decimal
                    suffix={<span className="text-xs text-muted-foreground">= {PCT(config.comision_delegacion_free)}</span>}
                    testid="cfg-com-free"/>
                </Field>
                <Field label="Plan Premium" hint="Comisión para plan Premium mensual/anual activo.">
                  <NumericInput value={config.comision_delegacion_premium} onChange={(v) => set("comision_delegacion_premium", v)}
                    step={0.001} min={0} max={1} decimal
                    suffix={<span className="text-xs text-muted-foreground">= {PCT(config.comision_delegacion_premium)}</span>}
                    testid="cfg-com-premium"/>
                </Field>
                <Field label="Pionero · durante reto (primeras 10)" hint="Comisión durante el reto Pionero (0% típicamente).">
                  <NumericInput value={config.comision_delegacion_pionero_activo} onChange={(v) => set("comision_delegacion_pionero_activo", v)}
                    step={0.001} min={0} max={1} decimal
                    suffix={<span className="text-xs text-muted-foreground">= {PCT(config.comision_delegacion_pionero_activo)}</span>}
                    testid="cfg-com-pionero-activo"/>
                </Field>
                <Field label="Pionero · tras el reto" hint="Comisión aplicada al Pionero después de completar el reto (5% típico).">
                  <NumericInput value={config.comision_delegacion_pionero_post} onChange={(v) => set("comision_delegacion_pionero_post", v)}
                    step={0.001} min={0} max={1} decimal
                    suffix={<span className="text-xs text-muted-foreground">= {PCT(config.comision_delegacion_pionero_post)}</span>}
                    testid="cfg-com-pionero-post"/>
                </Field>
                <Field label="Fundador" hint="Comisión perpetua para Fundadores/Embajadores.">
                  <NumericInput value={config.comision_delegacion_founder} onChange={(v) => set("comision_delegacion_founder", v)}
                    step={0.001} min={0} max={1} decimal
                    suffix={<span className="text-xs text-muted-foreground">= {PCT(config.comision_delegacion_founder)}</span>}
                    testid="cfg-com-founder"/>
                </Field>
              </div>
            </div>
          </Section>

          {/* ═══════════════ Colega IA ═══════════════ */}
          <Section
            id="ia"
            open={open.ia}
            onToggle={() => toggle("ia")}
            icon={<Sparkles size={18} className="text-cyan-300"/>}
            title="Colega IA"
            testid="cfg-section-ia"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Consultas incluidas por mes" hint="Cuántas consultas de Colega IA vienen incluidas en cada plan de pago (misma cantidad para Pionero/Premium/Fundador). Free no tiene acceso.">
                <NumericInput value={config.ai_included_queries} onChange={(v) => set("ai_included_queries", v)}
                  step={1} min={0} testid="cfg-ai-included"/>
              </Field>
              <Field label="Consultas por Pack Extra" hint="Cuántas consultas agrega cada Pack Extra que compra el usuario cuando agota su cuota mensual.">
                <NumericInput value={config.ai_extra_pack_size} onChange={(v) => set("ai_extra_pack_size", v)}
                  step={1} min={1} testid="cfg-ai-pack-size"/>
              </Field>
              <Field label="Precio Pack Extra" hint="Precio del Pack Extra. Se registra como UF y el sistema convierte a CLP con el valor UF del día (mindicador.cl). También podés escribirlo directamente en CLP.">
                <UfInput value={config.ai_extra_pack_uf} onChange={(v) => set("ai_extra_pack_uf", v)}
                  uf={ufValue} testid="cfg-ai-pack-uf"/>
              </Field>
              <Field label="Precio Pack Extra · override CLP (opcional)" hint="Si es > 0, el sistema cobra este monto fijo en CLP e ignora el cálculo UF. Dejar en 0 para usar UF del día.">
                <MoneyInput value={config.ai_extra_pack_clp_override} onChange={(v) => set("ai_extra_pack_clp_override", v)}
                  uf={ufValue} testid="cfg-ai-pack-clp"/>
              </Field>
            </div>
          </Section>

          <div className="p-3 rounded-md border border-amber-400/25 bg-amber-400/[0.05] text-xs text-amber-100 inline-flex items-start gap-2" data-testid="cfg-note-privacy">
            <ShieldAlert size={12} className="mt-0.5 shrink-0"/>
            <span>
              <strong>Privacidad garantizada:</strong> la persona solicitante sólo ve el total a pagar; nunca el desglose de honorario del profesional ni la comisión Colega Link.
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="cfg-save">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
              {saving ? "Aplicando…" : "Guardar Cambios en toda la plataforma"}
            </button>
            <button onClick={resetToDefaults} className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="cfg-reset">
              <RefreshCw size={12}/> Reponer defaults
            </button>
          </div>

          {meta.updated_at && (
            <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-1" data-testid="cfg-meta">
              <Check size={11}/> Última actualización: {new Date(meta.updated_at).toLocaleString("es-CL")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ open, onToggle, icon, title, children, testid }) {
  return (
    <div className="panel" data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <div className="inline-flex items-center gap-2">
          {icon}
          <span className="serif text-lg">{title}</span>
        </div>
        {open ? <ChevronDown size={16} className="text-muted-foreground"/> : <ChevronRight size={16} className="text-muted-foreground"/>}
      </button>
      {open && <div className="p-4 pt-0 space-y-4 border-t border-white/5">{children}</div>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function NumericInput({ value, onChange, step = 1, min, max, decimal = false, suffix, testid }) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value ?? 0}
        onChange={(e) => onChange(decimal ? toFloat(e.target.value) : toInt(e.target.value))}
        className="field w-40"
        data-testid={testid}
      />
      {suffix}
    </div>
  );
}

/**
 * MoneyInput · fuente de verdad = CLP (entero).
 * Muestra 2 campos ligados: [CLP] y [UF]. Editar uno actualiza el otro
 * usando `uf` = valor CLP de 1 UF (mindicador.cl del día). El valor persistido
 * en el estado siempre es el CLP entero — la UF se recalcula al vuelo.
 */
function MoneyInput({ value, onChange, uf, testid }) {
  const clp = toInt(value);
  const ufVal = uf > 0 ? clp / uf : 0;
  const onClpChange = (e) => onChange(toInt(e.target.value));
  const onUfChange = (e) => {
    const nUf = toFloat(e.target.value);
    onChange(Math.round(nUf * uf));
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label className="inline-flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">CLP</span>
        <input
          type="number" min={0} step={100} value={clp}
          onChange={onClpChange}
          className="field w-36"
          data-testid={testid}
        />
      </label>
      <span className="text-xs text-muted-foreground">↔</span>
      <label className="inline-flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">UF</span>
        <input
          type="number" min={0} step={0.01} value={Number(ufVal.toFixed(4))}
          onChange={onUfChange}
          className="field w-28"
          data-testid={`${testid}-uf`}
        />
      </label>
      <span className="text-[11px] text-muted-foreground">= {CLP(clp)} · {UF_FMT(ufVal)} UF</span>
    </div>
  );
}

/**
 * UfInput · fuente de verdad = UF (decimal).
 * Idéntico a MoneyInput pero al revés: el valor persistido es la UF, y el CLP
 * se calcula al vuelo. Editar cualquiera de los dos campos actualiza el otro.
 */
function UfInput({ value, onChange, uf, testid }) {
  const ufVal = toFloat(value);
  const clp = Math.round(ufVal * uf);
  const onUfChange = (e) => onChange(toFloat(e.target.value));
  const onClpChange = (e) => {
    const nClp = toInt(e.target.value);
    onChange(uf > 0 ? Number((nClp / uf).toFixed(4)) : 0);
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label className="inline-flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">UF</span>
        <input
          type="number" min={0} step={0.01} value={ufVal}
          onChange={onUfChange}
          className="field w-28"
          data-testid={testid}
        />
      </label>
      <span className="text-xs text-muted-foreground">↔</span>
      <label className="inline-flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">CLP</span>
        <input
          type="number" min={0} step={100} value={clp}
          onChange={onClpChange}
          className="field w-36"
          data-testid={`${testid}-clp`}
        />
      </label>
      <span className="text-[11px] text-muted-foreground">= {UF_FMT(ufVal)} UF · {CLP(clp)}</span>
    </div>
  );
}
